"use client";

import type { KeyboardEvent, ReactNode } from "react";
import { ChevronDown } from "@/components/icons";

import { Button } from "@/components/ui/button";
import { CONTROL_HEIGHT_CLASSES } from "@/lib/design-system/control-density";
import { cn } from "@/lib/utils";
import { isTypeAheadKey } from "./inline-combobox-keyboard";

function handleTriggerKey(event: KeyboardEvent<HTMLButtonElement>, onOpen: () => void, onType: (text: string) => void) {
  if (event.key === "ArrowDown") {
    event.preventDefault();
    onOpen();
  } else if (event.key !== " " && isTypeAheadKey(event)) {
    event.preventDefault();
    onType(event.key);
  }
}

export function valueTriggerClassName(disabled = false) {
  return cn(
    "-mx-1.5 block w-[calc(100%+0.75rem)] min-w-0 rounded-sm px-1.5 py-0.5 text-left text-sm text-foreground transition-colors focus-visible:outline-2 focus-visible:outline-ring disabled:cursor-default",
    !disabled && "cursor-pointer hover:bg-muted/50",
  );
}

interface ValueTriggerProps {
  id?: string;
  ariaLabel?: string;
  ariaInvalid?: boolean;
  ariaDescribedby?: string;
  disabled: boolean;
  className?: string;
  onOpen: () => void;
  onType: (text: string) => void;
  content: ReactNode;
  closedText: string;
  placeholder: string;
}

export function ValueTrigger({
  id,
  ariaLabel,
  ariaInvalid,
  ariaDescribedby,
  disabled,
  className,
  onOpen,
  onType,
  ...closed
}: ValueTriggerProps) {
  return (
    <button
      type="button"
      id={id}
      // oxlint-disable-next-line jsx-a11y/role-has-required-aria-props -- a collapsed combobox owns no popup; the listbox mounts in the open editor
      role="combobox"
      aria-label={ariaLabel}
      aria-invalid={ariaInvalid}
      aria-describedby={ariaDescribedby}
      aria-expanded={false}
      disabled={disabled}
      onClick={onOpen}
      onKeyDown={(event) => handleTriggerKey(event, onOpen, onType)}
      className={cn(valueTriggerClassName(disabled), "group flex items-start gap-1", className)}
    >
      <ClosedValue {...closed} disabled={disabled} />
      {disabled ? null : (
        <ChevronDown
          aria-hidden
          className="mt-0.5 size-3.5 shrink-0 text-muted-foreground/50 transition-colors group-hover:text-foreground"
        />
      )}
    </button>
  );
}

function ClosedValue({
  content,
  closedText,
  placeholder,
  disabled,
  quietPlaceholder = false,
}: {
  content: ReactNode;
  closedText: string;
  placeholder: string;
  disabled: boolean;
  quietPlaceholder?: boolean;
}) {
  return (
    <span className="min-w-0 flex-1">
      {content ??
        (closedText ? (
          <span className="block truncate">{closedText}</span>
        ) : (
          <span className={cn("block truncate", disabled || quietPlaceholder ? "text-fg-subtle" : "text-link")}>
            {placeholder}
          </span>
        ))}
    </span>
  );
}

interface CardTriggerProps extends ValueTriggerProps {
  onEdit?: () => void;
  onClear?: () => void;
  editLabel: string;
  clearLabel: string;
}

function CardTriggerActions({ onEdit, onClear, onOpen, editLabel, clearLabel }: {
  onEdit?: () => void;
  onClear?: () => void;
  onOpen: () => void;
  editLabel: string;
  clearLabel: string;
}) {
  return (
    <span className="flex shrink-0 items-center gap-3 text-sm">
      {onEdit ? (
        <Button variant="link" size="bare" onClick={onEdit} aria-label={editLabel}>
          Edit
        </Button>
      ) : null}
      {onClear ? (
        <Button variant="link" size="bare" onClick={onClear} aria-label={clearLabel}>
          Remove
        </Button>
      ) : null}
      <ChevronDown aria-hidden className="size-4 shrink-0 text-muted-foreground/70" onClick={onOpen} />
    </span>
  );
}

export function CardTrigger({ onEdit, onClear, editLabel, clearLabel, ...props }: CardTriggerProps) {
  const { id, ariaLabel, ariaInvalid, ariaDescribedby, disabled, className, onOpen, onType, ...closed } = props;
  return (
    <div
      className={cn(
        CONTROL_HEIGHT_CLASSES.default,
        "flex w-full min-w-0 items-center gap-2 rounded-field border border-input bg-card pr-2.5 transition-colors aria-invalid:border-destructive",
        !disabled && "hover:border-ring",
        disabled && "border-border-subtle bg-card",
        className,
      )}
      aria-invalid={ariaInvalid}
    >
      <button
        type="button"
        id={id}
        // oxlint-disable-next-line jsx-a11y/role-has-required-aria-props -- a collapsed combobox owns no popup; the listbox mounts in the open editor
        role="combobox"
        aria-label={ariaLabel}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedby}
        aria-expanded={false}
        disabled={disabled}
        onClick={onOpen}
        onKeyDown={(event) => handleTriggerKey(event, onOpen, onType)}
        className="flex min-w-0 flex-1 items-center gap-3 self-stretch rounded-l-field pl-3 text-left text-sm text-foreground focus-visible:outline-2 focus-visible:outline-ring disabled:cursor-default"
      >
        <ClosedValue {...closed} disabled={disabled} quietPlaceholder />
      </button>
      {disabled ? null : <CardTriggerActions onEdit={onEdit} onClear={onClear} onOpen={onOpen} editLabel={editLabel} clearLabel={clearLabel} />}
    </div>
  );
}
